import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  Card,
  Box,
  Typography,
  Stack,
  Tooltip,
  IconButton,
} from "@mui/material";
import {
  VolunteerActivismOutlined as DonationIcon,
  ShoppingCartOutlined as SaleIcon,
  ShareOutlined as ShareIcon,
  Check as CheckIcon,
  AccessTimeOutlined as ClockIcon,
  CheckCircleOutline as ActiveIcon,
  CancelOutlined as ExpiredIcon,
} from "@mui/icons-material";
import moment from "moment";
import { colors, borderRadiusPx, customShadows } from "../../../../../theme";
import { CommonChip } from "../../../../Components/ui";

const GiftCardDetailHero = ({
  cardId,
  isDonation = false,
  tokenValue = "0",
  tokenName = "BET",
  isSold = false,
  expDate = 0,
  onShare,
}) => {
  const [copied, setCopied] = useState(false);
  const currentDT = moment().unix();
  const expTimestamp = Number(expDate || 0);
  const isExpired = expTimestamp > 0 && expTimestamp <= currentDT;
  const expFormatted =
    expTimestamp > 0
      ? moment.unix(expTimestamp).format("DD MMM YYYY, HH:mm")
      : "No Expiry";
  const expRelative = expTimestamp > 0 ? moment.unix(expTimestamp).fromNow() : "";

  const handleShare = () => {
    if (onShare) {
      onShare();
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <Card
      sx={{
        p: { xs: 2.5, sm: 3.5 },
        borderRadius: borderRadiusPx.xl,
        backgroundColor: colors.dashboard.cardBg,
        border: `1px solid ${colors.dashboard.cardBorder}`,
        boxShadow: customShadows.cardFlat,
        position: "relative",
        overflow: "hidden",
        mb: 3.5,
      }}
    >
      {/* Top Accent Gradient Line */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: 4,
          background: isDonation
            ? `linear-gradient(135deg, ${colors.amber[400]} 0%, ${colors.amber[600]} 100%)`
            : colors.gradients.primary,
        }}
      />

      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2.5}
        alignItems={{ xs: "flex-start", sm: "center" }}
        justifyContent="space-between"
      >
        {/* Left Side: Header info & tags */}
        <Stack spacing={1.5} alignItems="flex-start">
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            {/* Card ID Badge */}
            <Box
              sx={{
                px: 1.25,
                py: 0.35,
                borderRadius: borderRadiusPx.sm,
                bgcolor: colors.slate[900],
                color: colors.neutral.white,
                fontSize: "0.75rem",
                fontWeight: 800,
                letterSpacing: "0.02em",
              }}
            >
              Card #{cardId}
            </Box>

            {/* Type Chip */}
            {isDonation ? (
              <CommonChip
                label="Free Voucher"
                startIcon={<DonationIcon sx={{ fontSize: 13 }} />}
                size="small"
                sx={{
                  bgcolor: colors.amber[50],
                  color: colors.amber[800],
                  fontWeight: 700,
                  border: `1px solid ${colors.amber[200]}`,
                }}
              />
            ) : (
              <CommonChip
                label="Direct Sale"
                startIcon={<SaleIcon sx={{ fontSize: 13 }} />}
                size="small"
                sx={{
                  bgcolor: colors.blue[50],
                  color: colors.primary.main,
                  fontWeight: 700,
                  border: `1px solid ${colors.blue[200]}`,
                }}
              />
            )}

            {/* Status Chip */}
            {isSold ? (
              <CommonChip
                label="Sold Out"
                size="small"
                sx={{
                  bgcolor: colors.slate[100],
                  color: colors.slate[700],
                  fontWeight: 700,
                  border: `1px solid ${colors.slate[300]}`,
                }}
              />
            ) : isExpired ? (
              <CommonChip
                label="Expired"
                startIcon={<ExpiredIcon sx={{ fontSize: 13 }} />}
                size="small"
                sx={{
                  bgcolor: colors.red[50],
                  color: colors.red[700],
                  fontWeight: 700,
                  border: `1px solid ${colors.red[200]}`,
                }}
              />
            ) : (
              <CommonChip
                label="Active & Available"
                startIcon={<ActiveIcon sx={{ fontSize: 13 }} />}
                size="small"
                sx={{
                  bgcolor: colors.emerald[50],
                  color: colors.emerald[700],
                  fontWeight: 700,
                  border: `1px solid ${colors.emerald[200]}`,
                }}
              />
            )}
          </Stack>

          {/* Title */}
          <Typography
            variant="h4"
            sx={{
              fontWeight: 900,
              color: colors.slate[900],
              fontSize: { xs: "1.5rem", sm: "1.85rem", md: "2.1rem" },
              letterSpacing: "-0.02em",
              textAlign: "left",
            }}
          >
            {tokenValue}{" "}
            <span style={{ color: colors.primary.main }}>{tokenName}</span>{" "}
            {isDonation ? "Gift Voucher" : "Gift Card"}
          </Typography>

          {/* Expiry Timestamp */}
          <Stack direction="row" spacing={0.75} alignItems="center">
            <ClockIcon sx={{ fontSize: 15, color: colors.slate[400] }} />
            <Typography
              variant="caption"
              sx={{
                fontWeight: 600,
                color: isExpired ? colors.red[600] : colors.slate[500],
                fontSize: "0.82rem",
              }}
            >
              {isExpired
                ? `Expired on ${expFormatted}`
                : `Valid until ${expFormatted} (${expRelative})`}
            </Typography>
          </Stack>
        </Stack>

        {/* Right Side: Share Action */}
        <Tooltip title={copied ? "Link Copied!" : "Share Gift Card Link"}>
          <IconButton
            onClick={handleShare}
            sx={{
              bgcolor: colors.slate[50],
              border: `1px solid ${colors.slate[200]}`,
              color: colors.slate[700],
              p: 1.25,
              borderRadius: borderRadiusPx.lg,
              transition: "all 0.2s ease-in-out",
              "&:hover": {
                bgcolor: colors.blue[50],
                color: colors.primary.main,
                borderColor: colors.blue[200],
              },
            }}
          >
            {copied ? (
              <CheckIcon sx={{ fontSize: 20, color: colors.emerald[600] }} />
            ) : (
              <ShareIcon sx={{ fontSize: 20 }} />
            )}
          </IconButton>
        </Tooltip>
      </Stack>
    </Card>
  );
};

GiftCardDetailHero.propTypes = {
  cardId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  isDonation: PropTypes.bool,
  tokenValue: PropTypes.string,
  tokenName: PropTypes.string,
  isSold: PropTypes.bool,
  expDate: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onShare: PropTypes.func,
};

export default GiftCardDetailHero;
